import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, Loader2, Download, ShieldAlert, FileJson, Box, Grid3x3, FileText, Boxes, Ban } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE } from "@/api/client";
import type { Dwelling, Validation, ExportJob } from "@/api/types";
import { StatusBadge } from "@/components/StatusBadge";

const FORMATS = [
  { id: "ifc", label: "IFC 4.0", desc: "BIM / architecture", icon: Boxes, testId: "export-ifc-button" },
  { id: "obj", label: "OBJ", desc: "Maillage 3D", icon: Box, testId: "export-obj-button" },
  { id: "dxf", label: "DXF 2D", desc: "Plan CAO", icon: Grid3x3, testId: "export-dxf-button" },
  { id: "json", label: "JSON Schema", desc: "Données structurées", icon: FileJson, testId: "export-json-button" },
  { id: "pdf", label: "PDF Summary", desc: "Rapport de synthèse", icon: FileText, testId: "export-pdf-button" },
];

export default function ExportPage() {
  const { dwellingId = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [force, setForce] = useState(false);

  const { data: dwelling } = useQuery<Dwelling>({ queryKey: ["dwelling", dwellingId], queryFn: () => api.getDwelling(dwellingId) });
  const { data: validations = [] } = useQuery<Validation[]>({
    queryKey: ["validations", dwelling?.project_id, dwellingId],
    enabled: !!dwelling,
    queryFn: async () => (await api.listValidations(dwelling!.project_id)).filter((v) => v.dwelling_id === dwellingId),
  });
  const { data: exports = [] } = useQuery<ExportJob[]>({ queryKey: ["exports", dwellingId], queryFn: () => api.listExports(dwellingId) });

  const exportMut = useMutation({
    mutationFn: (format: string) => api.createExport(dwellingId, format, force),
    onSuccess: (e) => {
      if (e.status === "blocked") toast.error(e.blocked_reason || "Export bloqué");
      else if (e.status === "completed") toast.success("Export généré");
      else toast.error("Échec de l'export");
      qc.invalidateQueries({ queryKey: ["exports", dwellingId] });
    },
    onError: (e: any) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  if (!dwelling) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const criticals = validations.filter((v) => v.severity === "critical" && ["pending", "rejected", "blocked"].includes(v.status));
  const blocked = criticals.length > 0;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(`/dwellings/${dwellingId}`)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Logement
      </button>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Centre d'export</h1>
        <p className="mt-1 text-sm text-muted-foreground">{dwelling.name} — exportez la géométrie validée vers les formats CAO/BIM.</p>
      </div>

      {blocked && (
        <div data-testid="export-blocked-alert" className="flex flex-col gap-3 rounded-xl border border-rose-500/40 bg-rose-500/10 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-6 w-6 animate-pulse text-rose-400" />
            <div>
              <div className="font-semibold text-rose-400">Export bloqué</div>
              <div className="text-sm text-muted-foreground">{criticals.length} incohérence(s) critique(s). Résolvez-les dans l'espace de validation.</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate(`/validation/${dwellingId}`)} className="rounded-md border border-border px-4 py-2 text-sm hover:border-primary/40">Aller à la validation</button>
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input data-testid="force-override-checkbox" type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} className="h-4 w-4 accent-rose-400" />
              Forcer (override)
            </label>
          </div>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FORMATS.map((f) => {
          const disabled = blocked && !force;
          return (
            <button
              key={f.id}
              data-testid={f.testId}
              onClick={() => exportMut.mutate(f.id)}
              disabled={disabled || exportMut.isPending}
              className={`group rounded-xl border p-5 text-left transition-all ${disabled ? "cursor-not-allowed border-border/40 opacity-50" : "border-border/60 bg-card hover:border-primary/40 hover:bg-[#1A2234]"}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 border border-primary/20">
                  {disabled ? <Ban className="h-5 w-5 text-rose-400" /> : <f.icon className="h-5 w-5 text-primary" />}
                </div>
                <Download className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
              </div>
              <div className="mt-4 text-lg font-semibold">{f.label}</div>
              <div className="uppercase-label text-muted-foreground">{f.desc}</div>
            </button>
          );
        })}
      </div>

      <div className="rounded-xl border border-border/60 bg-card p-5">
        <h3 className="mb-3 text-lg font-semibold">Historique des exports ({exports.length})</h3>
        {exports.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun export encore.</p>
        ) : (
          <div className="space-y-2">
            {exports.map((e) => (
              <div key={e._id} data-testid={`export-row-${e._id}`} className="flex flex-col gap-2 rounded-lg border border-border/50 bg-background/40 p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <span className="font-mono uppercase">{e.format}</span>
                  <StatusBadge status={e.status} testId={`export-status-${e._id}`} />
                  {e.forced && <span className="uppercase-label text-rose-400">forcé</span>}
                </div>
                <div className="flex items-center gap-3">
                  {e.blocked_reason && <span className="text-xs text-rose-400">{e.blocked_reason}</span>}
                  {e.status === "completed" && e.file_path && (
                    <a data-testid={`export-download-${e._id}`} href={`${API_BASE}/files/${e.file_path}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-semibold text-primary-foreground hover:opacity-90">
                      <Download className="h-4 w-4" /> Télécharger
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
